import axiosInstance from '../../shared/services/axiosConfig';
import type {
  MailboxAttachment,
  MailboxDraft,
  MailboxMessage,
  MailboxSummary,
  MailboxThread,
  ThreadFolder,
} from './types';

export async function getMyMailbox(): Promise<MailboxSummary | null> {
  try {
    const { data } = await axiosInstance.get('/mailboxes/me');
    return data.mailbox ?? null;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function listThreads(
  mailboxId: string,
  params: { cursor?: string; unread?: boolean; folder?: ThreadFolder; q?: string } = {}
) {
  const { data } = await axiosInstance.get(`/mailboxes/${mailboxId}/threads`, { params });
  return data as { threads: MailboxThread[]; nextCursor?: string };
}

export async function getThread(mailboxId: string, threadId: string) {
  const { data } = await axiosInstance.get(`/mailboxes/${mailboxId}/threads/${threadId}`);
  return data as { thread: MailboxThread; messages: MailboxMessage[]; attachments: MailboxAttachment[] };
}

export async function markRead(mailboxId: string, threadId: string) {
  await axiosInstance.post(`/mailboxes/${mailboxId}/threads/${threadId}/read`);
}

export async function setSpam(mailboxId: string, threadId: string, spam: boolean) {
  await axiosInstance.post(`/mailboxes/${mailboxId}/threads/${threadId}/spam`, { spam });
}

export async function markUnread(mailboxId: string, threadId: string) {
  await axiosInstance.post(`/mailboxes/${mailboxId}/threads/${threadId}/unread`);
}

export async function setTrash(mailboxId: string, threadId: string, trash: boolean) {
  await axiosInstance.post(`/mailboxes/${mailboxId}/threads/${threadId}/trash`, { trash });
}

export async function deleteForever(mailboxId: string, threadId: string) {
  await axiosInstance.delete(`/mailboxes/${mailboxId}/threads/${threadId}`);
}

export async function emptyTrash(mailboxId: string) {
  const { data } = await axiosInstance.post(`/mailboxes/${mailboxId}/threads/empty-trash`);
  return Number(data.deleted || 0);
}

export type DraftPayload = {
  to: string[];
  cc: string[];
  subject: string;
  text: string;
  forwardAttachmentIds?: string[];
};

export async function listDrafts(mailboxId: string): Promise<MailboxDraft[]> {
  const { data } = await axiosInstance.get(`/mailboxes/${mailboxId}/drafts`);
  return data.drafts || [];
}

export async function getDraft(mailboxId: string, draftId: string) {
  const { data } = await axiosInstance.get(`/mailboxes/${mailboxId}/drafts/${draftId}`);
  return data as { draft: MailboxDraft; attachments: MailboxAttachment[] };
}

export async function saveDraft(mailboxId: string, draftId: string | undefined, payload: DraftPayload): Promise<MailboxDraft> {
  const { data } = draftId
    ? await axiosInstance.put(`/mailboxes/${mailboxId}/drafts/${draftId}`, payload)
    : await axiosInstance.post(`/mailboxes/${mailboxId}/drafts`, payload);
  return data.draft;
}

export async function deleteDraft(mailboxId: string, draftId: string) {
  await axiosInstance.delete(`/mailboxes/${mailboxId}/drafts/${draftId}`);
}

export async function getUnreadCount(mailboxId: string) {
  const { data } = await axiosInstance.get(`/mailboxes/${mailboxId}/unread-count`);
  return Number(data.unread_count || 0);
}

export async function sendMessage(
  mailboxId: string,
  payload: {
    to: string[];
    cc?: string[];
    subject: string;
    text: string;
    threadId?: string;
    inReplyTo?: string;
    draftId?: string;
    forwardAttachmentIds?: string[];
    attachments?: Array<{ filename: string; contentType: string; contentBase64: string }>;
  }
) {
  const { data } = await axiosInstance.post(`/mailboxes/${mailboxId}/messages`, payload);
  return data;
}

export function attachmentUrl(mailboxId: string, attId: string) {
  return `/mailboxes/${mailboxId}/attachments/${attId}`;
}

/** Returns the attachment as a Blob typed with the stored content type (needed for in-browser preview). */
export async function fetchAttachmentBlob(mailboxId: string, att: MailboxAttachment): Promise<Blob> {
  const { data } = await axiosInstance.get<Blob>(attachmentUrl(mailboxId, att._id), { responseType: 'blob' });
  return new Blob([data], { type: att.content_type || data.type || 'application/octet-stream' });
}
